import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
// import { Menu, Search, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import styled from 'styled-components';
import DeleteIcon from '../../../../assets/icons/DeleteIcon';
import MenuIcon from '../../../../assets/icons/MenuIcon';
import ArrowIcon from '../../../../assets/icons/ArrowIcon';
import SearchIcon from '../../../../assets/icons/SearchIcon';
import { useToast } from '../../../../components/toast/ToastContext';
import { CorporateTheme, CreateFormTheme, Theme } from '../../../../utils/themes';
import { useTheme } from '../../../../middleware/ThemeContext';
import { StyledHorizontalLine } from '../../../../components/StyledComponents/Styledhr';
import StyledText from '../../../../components/StyledComponents/StyledText';
import DraggableIcon from '../../../../assets/icons/DraggableIcon';
import SearchBar from '../../../../components/searchBar/SearchBar';
import SecTitleWithIconDesc from '../../components/secTitleWithIconDesc';
import CodeIcon from '../../../../assets/icons/CodeIcon';
import MenuStackIcon from '../../../../assets/icons/MenuStackIcon';
import ChevronDownIcon from '../../../../assets/icons/ChevronDownIcon';
import { capitalizeFirstLetter, hexToRgba } from '../../../../utils/helper';
import { useGetMenusQuery, useGetPagesQuery } from '../../../../api/pageBuilderApiSlice';
import { MenuBuilderRef } from '../Builder';
import { debounce } from 'lodash';
import { array } from 'yup';
import { SkeletonCell } from '../../../../components/SkeletonCell';
import TooltipIcon from '../../../../assets/icons/Tooltip';
import DropzoneTooltipIcon from '../../../../assets/icons/DropzoneTooltip';
import IdeaIcon from '../../../../assets/icons/IdeaIcon';
import Badge from '../../../../components/Badge';

interface Page {
    id: string;
    name: string;
    url: string;
    pageType?: string;
}

interface MenuItem extends Page {
    children?: MenuItem[];
}

interface DropZone {
    menuType: string;
    index: number;
    parentId?: string;
    isChildZone?: boolean;
}

// Styled Components
const Container = styled.div`
  display: flex;
  gap: 20px;
  height: calc(100vh - 225px);
`;

const LeftPanel = styled.div<{ theme: Theme }>`
  width: 349px;
  display: flex;
  flex-direction: column;
  border-radius:10px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
`;

const PanelHeader = styled.div`
  padding: 1rem;
//   border-bottom: 1px solid #dee2e6;

  h2 {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }

  p {
    font-size: 0.75rem;
    color: #6c757d;
    margin: 0;
  }
`;

const SearchWrapper = styled.div`
  padding: 1rem;
    
`;

const PageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
`;

const PageItem = styled.div<{ isDragging?: boolean, theme: Theme }>`
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  border-radius: 0.375rem;
  cursor: move;
  transition: background-color 0.2s;
  opacity: ${props => props.isDragging ? 0.5 : 1};

  &:hover {
    background-color: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
  }

 /* icon hidden by default */
  .hover-icon {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease;
  }

  /* ONLY hovered item shows icon */
  &:hover .hover-icon {
    opacity: 1;
    visibility: visible;
  }

`;

const RightPanel = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const MenuSection = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
//   margin-bottom: 1rem;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    color: #6c757d;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .menu-info {
    flex: 1;

    h3 {
      font-weight: 600;
      font-size: 0.875rem;
      margin: 0 0 0.25rem 0;
    }

    p {
      font-size: 0.75rem;
      color: #6c757d;
      margin: 0;
    }
  }

  .toggle-icon {
    width: 20px;
    height: 20px;
    color: #6c757d;
  }
`;

const MenuContent = styled.div`
  min-height: 100px;
`;

const DropZonePair = styled.div`
  display: flex;
  gap: 0.75rem;
  margin: 0.75rem 0;
  padding-left: 34px;
`;

const DropZone = styled.div<{
    type: 'parent' | 'child', aboveItemId?: string; fullWidth?: boolean;
}>`
position: relative;
  height: 75px;
 width: ${({ type, aboveItemId, fullWidth }) => {
        if (type === 'parent') {
            if (fullWidth) return '100%';

            return aboveItemId ? '79px' : '100%';
        }
        // Child always full width
        return '100%';
    }};
  background-color: ${hexToRgba('#0d6efd', 0.10)};
  border: 1px dashed #0d6efd;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  }
`;

const MenuItemWrapper = styled.div<{ isChild?: boolean; isDragging?: boolean }>`
  margin-left: ${props => props.isChild ? '2rem' : '0'};
  opacity: ${props => props.isDragging ? 0.4 : 1};
  position: relative;
`;

const MenuItemCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0px;  
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  transition: border-color 0.2s;

  &:hover {
    border-color: #0d6efd;

    .delete-btn {
      opacity: 1;
    }
  }

  .item-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;

    .grip-icon {
      width: 16px;
      height: 16px;
      color: #6c757d;
      cursor: move;
    }

    .item-info {
      .item-name {
        font-weight: 500;
        font-size: 0.875rem;
      }

      .item-path {
        font-size: 0.75rem;
        color: #6c757d;
      }
    }
  }

  .delete-btn {
    opacity: 0;
    padding: 0.25rem;
    border: none;
    background: transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: #fef2f2;

      svg {
        color: #dc3545;
      }
    }

    svg {
      width: 16px;
      height: 16px;
      color: #6c757d;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-size: 0.875rem;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
`;

const ItemContent = styled.div<{ theme: Theme }>`
display: flex;
align-items: center;
width: 100%;
gap:15px;

`;

// NEW: Styled component for child drop zones
const ChildDropZone = styled.div`
  height: 40px;
  margin: 4px 0;
  background-color: ${hexToRgba('#0d6efd', 0.10)};
  border: 1px dashed #0d6efd;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
  color: #0d6efd;
`;

const DropzoneTooltip = styled.div`
 background-color: ${hexToRgba('#F59E0B', 0.10)};
  border: 1px solid ${hexToRgba('#F59E0B', 1)}; 
  border-radius: 5px;
    backdrop-filter: blur(20px);

`

type MenuBuilderProps = {
    createMenu: (data: any) => any;
    refreshToken?: number;
};

const MenuBuilder = forwardRef<MenuBuilderRef, MenuBuilderProps>((props, ref) => {
    const { refreshToken } = props;
    const { theme } = useTheme();
    const showToast = useToast();
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const autoScrollRef = useRef<{
        direction: 'up' | 'down' | null;
    }>({ direction: null });

    const rafIdRef = useRef<number | null>(null);
    const { data: menus, refetch: menuRefetch, isLoading: isLoadingMenus, isFetching: isFetchingMenus } = useGetMenusQuery(undefined, { refetchOnMountOrArgChange: true });
    const [utilityMenu, setUtilityMenu] = useState<MenuItem[]>([]);
    const [primaryMenu, setPrimaryMenu] = useState<MenuItem[]>([]);
    const [secondaryMenu, setSecondaryMenu] = useState<MenuItem[]>([]);

    const [originalUtilityMenu, setOriginalUtilityMenu] = useState<MenuItem[]>([]);
    const [originalPrimaryMenu, setOriginalPrimaryMenu] = useState<MenuItem[]>([])
    const [originalSecondaryMenu, setOriginalSecondaryMenu] = useState<MenuItem[]>([]);

    const [draggedItem, setDraggedItem] = useState<Page | null>(null);
    const [draggedFrom, setDraggedFrom] = useState<{
        menuType: string; item: MenuItem; parentId?: string;
    } | null>(null);
    const [activeDropZone, setActiveDropZone] = useState<DropZone | null>(null);
    const [hoveredDropZone, setHoveredDropZone] = useState<{
        menuType: string;
        index: number;
        type: 'parent' | 'child';
        parentId?: string;
    } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
        utility: true,
        primary: true,
        secondary: true,
    });
    const { data: publishedPages, refetch: publishedPagesRefetch, isLoading, isFetching } = useGetPagesQuery({ query: debouncedSearchTerm, statusId: 38 }, { refetchOnMountOrArgChange: true });
    const isMenuLoading = isLoadingMenus || isFetchingMenus;

    useEffect(() => {
        menuRefetch();
        publishedPagesRefetch();
    }, [refreshToken, menuRefetch, publishedPagesRefetch]);

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setDebouncedSearchTerm(value);
        }, 500), // 500ms delay
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        debouncedSearch(value);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);


    useEffect(() => {
        if (menus?.menus) {
            menus.menus.forEach((menu: any) => {
                const transformedPages: MenuItem[] = menu.pages.map((pageItem: any) => {
                    const menuItem: MenuItem = {
                        id: pageItem.page.id.toString(),
                        name: pageItem.page.name,
                        url: pageItem.page.url,
                        pageType: pageItem.page.pageType
                    };

                    // Transform children
                    if (pageItem.children && pageItem.children.length > 0) {
                        menuItem.children = pageItem.children.map((child: any) => ({
                            id: child.page.id.toString(),
                            name: child.page.name,
                            url: child.page.url,
                            pageType: child.page.pageType
                        }));
                    }

                    return menuItem;
                });

                // Set based on accessKey
                if (menu.accessKey === 'header') {
                    setPrimaryMenu(transformedPages); setOriginalPrimaryMenu(JSON.parse(JSON.stringify(transformedPages)));
                }
                else if (menu.accessKey === 'footer') {
                    setSecondaryMenu(transformedPages); setOriginalSecondaryMenu(JSON.parse(JSON.stringify(transformedPages)));
                }
                else if (menu.accessKey === 'utility') { setUtilityMenu(transformedPages); setOriginalUtilityMenu(JSON.parse(JSON.stringify(transformedPages))); }
            });
        }
    }, [menus]);

    const hasUnsavedChanges = useCallback(() => {
        return JSON.stringify(primaryMenu) !== JSON.stringify(originalPrimaryMenu)
            || JSON.stringify(secondaryMenu) !== JSON.stringify(originalSecondaryMenu)
            || JSON.stringify(utilityMenu) !== JSON.stringify(originalUtilityMenu);
    }, [primaryMenu, originalPrimaryMenu, secondaryMenu, originalSecondaryMenu, utilityMenu, originalUtilityMenu]);

    const getMenuState = (menuType: string): [MenuItem[], React.Dispatch<React.SetStateAction<MenuItem[]>>] => {
        switch (menuType) {
            case 'utility': return [utilityMenu, setUtilityMenu];
            case 'primary': return [primaryMenu, setPrimaryMenu];
            case 'secondary': return [secondaryMenu, setSecondaryMenu];
            default: return [[], () => { }];
        }
    };

    // Check if page already exists in menu (including children)
    const normalizePageId = (pageId: string | number) => String(pageId);

    const pageExistsInMenu = (menu: MenuItem[], pageId: string | number): boolean => {
        const normalizedId = normalizePageId(pageId);
        for (const item of menu) {
            if (normalizePageId(item.id) === normalizedId) return true;
            if (item.children && pageExistsInMenu(item.children, normalizedId)) return true;
        }
        return false;
    };


    const getItemMaxDepth = (item: MenuItem): number => {
        if (!item.children || item.children.length === 0) return 1;
        return 1 + Math.max(...item.children.map(getItemMaxDepth));
    };

    const findItemDepth = (menu: MenuItem[], id: string, currentDepth = 1): number | null => {
        for (const item of menu) {
            if (item.id === id) return currentDepth;
            if (item.children) {
                const found = findItemDepth(item.children, id, currentDepth + 1);
                if (found !== null) return found;
            }
        }
        return null;
    };

    const startAutoScroll = () => {
        if (rafIdRef.current !== null) return;

        const step = () => {
            if (!rightPanelRef.current) return;

            const panel = rightPanelRef.current;

            if (autoScrollRef.current.direction === 'up') {
                panel.scrollTop -= 12;
            }

            if (autoScrollRef.current.direction === 'down') {
                panel.scrollTop += 12;
            }

            rafIdRef.current = requestAnimationFrame(step);
        };

        rafIdRef.current = requestAnimationFrame(step);
    };

    const stopAutoScroll = () => {
        autoScrollRef.current.direction = null;

        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    };


    // Auto-scroll handler
    const handleAutoScroll = (e: React.DragEvent) => {
        if (!rightPanelRef.current) return;

        const panel = rightPanelRef.current;
        const rect = panel.getBoundingClientRect();

        const threshold = 120; // edge sensitivity
        const mouseY = e.clientY;

        if (mouseY < rect.top + threshold) {
            autoScrollRef.current.direction = 'up';
            startAutoScroll();
        }
        else if (mouseY > rect.bottom - threshold) {
            autoScrollRef.current.direction = 'down';
            startAutoScroll();
        }
        else {
            stopAutoScroll();
        }
    };


    const handleDragStart = (e: React.DragEvent, page: Page, fromMenu?: { menuType: string; item: MenuItem; parentId?: string }) => {
        if (fromMenu) {
            setDraggedFrom(fromMenu);
        } else {
            setDraggedItem(page);
        }
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDraggedFrom(null);
        setActiveDropZone(null);
        setHoveredDropZone(null);
        stopAutoScroll();
    };

    const removeItemFromMenu = (menu: MenuItem[], itemId: string): { newMenu: MenuItem[], parentId?: string } => {
        let foundParentId: string | undefined = undefined;

        const filtered = menu.filter(item => {
            if (item.id === itemId) return false;
            if (item.children) {
                const childIndex = item.children.findIndex(c => c.id === itemId);
                if (childIndex !== -1) {
                    foundParentId = item.id;
                    item.children = item.children.filter(c => c.id !== itemId);
                } else {
                    const result = removeItemFromMenu(item.children, itemId);
                    item.children = result.newMenu;
                    if (result.parentId) foundParentId = result.parentId;
                }
            }
            return true;
        });

        return { newMenu: filtered, parentId: foundParentId };
    };

    const transformMenuToPayload = (menuItems: MenuItem[]) => {
        return menuItems.map((item) => {
            // Step 1: Create base page object with pageId
            const pageItem: any = {
                pageId: parseInt(item.id)  // Convert "19" → 19
            };

            // Step 2: Add children if they exist
            if (item.children && item.children.length > 0) {
                pageItem.children = item.children.map((child) => ({
                    pageId: parseInt(child.id)  // Convert "18" → 18
                }));
            }

            // Step 3: Return transformed item
            return pageItem;
        });
    };

    const handleSubmit = async () => {
        const payload = {
            menus: [
                {
                    accessKey: "header",
                    name: "Main Navigation",
                    pages: transformMenuToPayload(primaryMenu)
                },
                {
                    accessKey: "footer",
                    name: "Footer Menu",
                    pages: transformMenuToPayload(secondaryMenu)
                },
                {
                    accessKey: "utility",
                    name: "Utility Links",
                    pages: transformMenuToPayload(utilityMenu)
                }
            ]
        };
        try {
            await props.createMenu(payload).unwrap();
            setOriginalPrimaryMenu(JSON.parse(JSON.stringify(primaryMenu)));
            setOriginalSecondaryMenu(JSON.parse(JSON.stringify(secondaryMenu)));
            setOriginalUtilityMenu(JSON.parse(JSON.stringify(utilityMenu)));
            menuRefetch();
            showToast("Success", "Menu updated successfully!", "success");
        } catch (error) {
            showToast("Error", "Failed to save menu", "error");
        }
    };

    const handleCancel = () => {
        setPrimaryMenu(originalPrimaryMenu);
        setSecondaryMenu(originalSecondaryMenu);
        setUtilityMenu(originalUtilityMenu);
    }

    useImperativeHandle(ref, () => ({
        handleSubmit,
        handleCancel,
        hasUnsavedChanges
    }), [handleSubmit, handleCancel, hasUnsavedChanges]);

    const handleDrop = (menuType: string, index: number, asChild: boolean, parentId?: string, isChildReorder?: boolean) => {
        const itemToAdd = draggedFrom?.item || draggedItem;
        if (!itemToAdd) return;

        const [menu, setMenu] = getMenuState(menuType);
        let newMenu = [...menu];
        let adjustedIndex = index;

        // Check for duplicates (prevent adding same page twice)
        if (pageExistsInMenu(newMenu, itemToAdd.id)) {
            const isSameMenuMove = draggedFrom && draggedFrom.menuType === menuType;
            if (!isSameMenuMove) {
                showToast("Info", 'This page is already in the menu!', 'info');
                setDraggedItem(null);
                setDraggedFrom(null);
                setActiveDropZone(null);
                return;
            }
        }


        if (draggedFrom) {
            const [sourceMenu, setSourceMenu] = getMenuState(draggedFrom.menuType);

            // ✅ FIX: Get oldIndex BEFORE removing the item
            let oldChildIndex = -1;
            if (isChildReorder && parentId && draggedFrom.parentId === parentId) {
                const parent = sourceMenu.find(item => item.id === parentId);
                if (parent?.children) {
                    oldChildIndex = parent.children.findIndex(c => c.id === draggedFrom.item.id);
                }
            }

            const result = removeItemFromMenu(sourceMenu, draggedFrom.item.id);
            setSourceMenu(result.newMenu);

            if (draggedFrom.menuType === menuType) {
                newMenu = result.newMenu;

                // Adjust index for reordering
                if (isChildReorder && parentId && draggedFrom.parentId === parentId) {
                    // Reordering within same parent's children
                    // ✅ Use the oldChildIndex we calculated before removal
                    if (oldChildIndex !== -1 && oldChildIndex < index) {
                        adjustedIndex = index - 1;
                    }
                } else if (!result.parentId && !draggedFrom.parentId) {
                    // Reordering at parent level
                    const draggedItemIndex = menu.findIndex(item => item.id === draggedFrom.item.id);
                    if (draggedItemIndex !== -1 && draggedItemIndex < index) {
                        adjustedIndex = index - 1;
                    }
                }
            }
        }

        // const newItem = { ...itemToAdd, children: itemToAdd.children || undefined };
        const newItem: MenuItem = draggedFrom?.item
            ? { ...itemToAdd, children: (itemToAdd as MenuItem).children }
            : { ...itemToAdd, children: undefined };

        let liftedChildren: MenuItem[] | null = null;

        if (asChild && parentId) {
            const parentDepth = findItemDepth(newMenu, parentId) ?? 0;
            const newItemDepth = getItemMaxDepth(newItem);
            const totalDepth = parentDepth + newItemDepth;

            if (parentDepth >= 2) {
                showToast("Info", "Maximum menu depth is 2 levels.", "info");
                setDraggedItem(null);
                setDraggedFrom(null);
                setActiveDropZone(null);
                return;
            }

            if (totalDepth > 2) {
                const childrenToLift = newItem.children ?? [];
                const maxChildDepth = childrenToLift.length
                    ? Math.max(...childrenToLift.map(getItemMaxDepth))
                    : 0;

                if (childrenToLift.length === 0 || parentDepth + maxChildDepth > 2) {
                    showToast("Info", "Maximum menu depth is 2 levels.", "info");
                    setDraggedItem(null);
                    setDraggedFrom(null);
                    setActiveDropZone(null);
                    return;
                }

                liftedChildren = childrenToLift;
                newItem.children = undefined;
            }
        }

        // NEW: Handle child reordering
        if (isChildReorder && parentId) {
            const parentIndex = newMenu.findIndex(item => item.id === parentId);
            if (parentIndex !== -1) {
                if (!newMenu[parentIndex].children) {
                    newMenu[parentIndex].children = [];
                }
                newMenu[parentIndex].children!.splice(adjustedIndex, 0, newItem);
            }
        } else if (asChild && parentId) {
            // Add as child
            const parentIndex = newMenu.findIndex(item => item.id === parentId);
            if (parentIndex !== -1) {
                if (!newMenu[parentIndex].children) {
                    newMenu[parentIndex].children = [];
                }
                newMenu[parentIndex].children!.push(newItem);

                if (liftedChildren && liftedChildren.length > 0) {
                    const insertAt = newMenu[parentIndex].children!.length;
                    newMenu[parentIndex].children!.splice(insertAt, 0, ...liftedChildren);
                }
            }
        } else {
            // Add as parent
            newMenu.splice(adjustedIndex, 0, newItem);
        }

        setMenu(newMenu);
        setDraggedItem(null);
        setDraggedFrom(null);
        setActiveDropZone(null);
    };

    const deleteItem = (menuType: string, itemId: string, parentId?: string) => {
        const [menu, setMenu] = getMenuState(menuType);
        if (parentId) {
            const newMenu = menu.map(item => {
                if (item.id === parentId && item.children) {
                    return { ...item, children: item.children.filter(child => child.id !== itemId) };
                }
                return item;
            });
            setMenu(newMenu);
        } else {
            setMenu(menu.filter(item => item.id !== itemId));
        }
    };

    const filteredPages = publishedPages?.pages?.filter((page: any) =>
        page.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const isDragging = draggedItem !== null || draggedFrom !== null;

    const renderDropZonePair = (menuType: string, index: number, aboveItemId?: string, isLastChild?: boolean) => {
        const canHaveChildren = (menuType: string) => menuType === 'primary';

        const isActive = activeDropZone?.menuType === menuType && activeDropZone?.index === index &&
            !activeDropZone?.isChildZone;

        if (!isDragging || !isActive) return null;

        // Don't show drop zones for the dragged item's own position when reordering
        let showParentZone = true;
        let showChildZone = true;

        // 🔴 IMPORTANT: utility / secondary => NO child zone
        if (!canHaveChildren(menuType)) {
            showChildZone = false;
        }

        const isPrimary = canHaveChildren(menuType);

        if (draggedFrom && draggedFrom.menuType === menuType) {
            const [currentMenu] = getMenuState(menuType);
            const draggedItemIndex = currentMenu.findIndex(item => item.id === draggedFrom.item.id);

            if (index === draggedItemIndex || index === draggedItemIndex + 1) {
                // return null;
                showParentZone = false;
            }
            // Hide CHILD zone only if trying to make item its own child (aboveItemId is the dragged item)
            if (aboveItemId === draggedFrom.item.id) {
                showChildZone = false;
            }
        }

        // **NEW: For last child position, show BOTH zones**
        if (isLastChild && isPrimary && aboveItemId) {
            showParentZone = true;
            showChildZone = true;
        }

        // If both zones are hidden, return null
        if (!showParentZone && !showChildZone) return null;

        return (
            <DropZonePair>
                {showParentZone && <DropZone
                    type="parent"
                    fullWidth={!isPrimary}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDrop(menuType, index, false);
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setHoveredDropZone({ menuType, index, type: 'parent' });
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        setHoveredDropZone(null);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHoveredDropZone(null);
                        handleDrop(menuType, index, false);
                    }}
                    aboveItemId={aboveItemId}
                >
                    {hoveredDropZone?.menuType === menuType &&
                        hoveredDropZone?.index === index &&
                        hoveredDropZone?.type === 'parent' ? (
                        <DropzoneTooltip
                            style={{
                                position: 'absolute',
                                top: '100%',
                                marginTop: '8px',
                                left: 0,
                                boxSizing: 'border-box',
                                padding: '8px 12px',
                                whiteSpace: 'nowrap',
                                zIndex: 999
                            }}
                        >
                            <div className='d-flex align-items-center gap-2'>
                                <DropzoneTooltipIcon />
                                <StyledText fontSize='13px' fontStyle='italic' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                    Drop here will become first level menu item
                                </StyledText>
                            </div>
                        </DropzoneTooltip>
                    ) : null}
                </DropZone>}

                {isPrimary && aboveItemId && showChildZone && (
                    <DropZone
                        type="child"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDrop(menuType, index, true, aboveItemId);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setHoveredDropZone({ menuType, index, type: 'child', parentId: aboveItemId });
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            setHoveredDropZone(null);
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setHoveredDropZone(null);
                            handleDrop(menuType, index, true, aboveItemId);
                        }}
                    >
                        {hoveredDropZone?.menuType === menuType &&
                            hoveredDropZone?.index === index &&
                            hoveredDropZone?.type === 'child' &&
                            hoveredDropZone?.parentId === aboveItemId ? (
                            <DropzoneTooltip
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    marginTop: '8px',
                                    left: 100,
                                    boxSizing: 'border-box',
                                    padding: '8px 12px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 999
                                }}
                            >
                                <div className='d-flex align-items-center gap-2'>
                                    <IdeaIcon size={18} />
                                    <StyledText fontSize='13px' fontStyle='italic' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                        Drop here will become sub level menu item
                                    </StyledText>
                                </div>
                            </DropzoneTooltip>
                        ) : null}
                    </DropZone>
                )}
            </DropZonePair>
        );
    };

    // **FIXED: Child drop zone rendering with proper logic**
    const renderChildDropZone = (
        menuType: string,
        parentId: string,
        childIndex: number,
        totalChildren: number
    ) => {
        if (!isDragging) return null;

        // ✅ FIX: Don't render ChildDropZone for the position AFTER last child
        // That position uses renderDropZonePair instead (to show BOTH zones)
        if (childIndex === totalChildren) {
            return null;
        }

        const isActive = activeDropZone?.menuType === menuType &&
            activeDropZone?.parentId === parentId &&
            activeDropZone?.index === childIndex &&
            activeDropZone?.isChildZone;

        // Don't show drop zone if dragging the child that's directly above this position
        if (draggedFrom &&
            draggedFrom.menuType === menuType &&
            draggedFrom.parentId === parentId) {
            const [menu] = getMenuState(menuType);
            const parent = menu.find(item => item.id === parentId);
            if (parent?.children) {
                const draggedChildIndex = parent.children.findIndex(c => c.id === draggedFrom.item.id);
                if (draggedChildIndex === childIndex || draggedChildIndex === childIndex - 1) {
                    return null;
                }
            }
        }

        if (!isActive) return null;

        return (
            // <ChildDropZone
            //     onDragOver={(e) => {
            //         e.preventDefault();
            //         e.stopPropagation();
            //     }}
            //     onDrop={(e) => {
            //         e.preventDefault();
            //         e.stopPropagation();
            //         handleDrop(menuType, childIndex, false, parentId, true);
            //     }}
            //     onClick={(e) => {
            //         e.preventDefault();
            //         e.stopPropagation();
            //         handleDrop(menuType, childIndex, false, parentId, true);
            //     }}
            // >
            //     Drop here to reorder
            // </ChildDropZone>

            <div style={{ marginLeft: 35 }}>
                <DropZone
                    type="child"
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHoveredDropZone({ menuType, index: childIndex, type: 'child', parentId });
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        setHoveredDropZone(null);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHoveredDropZone(null);
                        handleDrop(menuType, childIndex, false, parentId, true);
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDrop(menuType, childIndex, false, parentId, true);
                    }}
                >
                    {hoveredDropZone?.menuType === menuType &&
                        hoveredDropZone?.index === childIndex &&
                        hoveredDropZone?.type === 'child' &&
                        hoveredDropZone?.parentId === parentId ? (
                        <DropzoneTooltip
                            style={{
                                position: 'absolute',
                                top: '100%',
                                marginTop: '8px',
                                left: 100,
                                boxSizing: 'border-box',
                                padding: '8px 12px',
                                whiteSpace: 'nowrap',
                                zIndex: 999
                            }}
                        >
                            <div className='d-flex align-items-center gap-2'>
                                <IdeaIcon size={18} />
                                <StyledText fontSize='13px' fontStyle='italic' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                    Drop here will become sub level menu item
                                </StyledText>
                            </div>
                        </DropzoneTooltip>
                    ) : null}
                </DropZone>
            </div>
        );
    };

    const renderMenuItem = (item: MenuItem, menuType: string, index: number, items: MenuItem[], parentId?: string) => {

        const isBeingDragged = draggedFrom?.item.id === item.id;

        return (
            <div key={item.id}>
                {!parentId && renderDropZonePair(
                    menuType,
                    index,
                    index > 0 ? items[index - 1].id : undefined
                )}

                <MenuItemWrapper
                    isChild={!!parentId}
                    isDragging={isBeingDragged}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!isDragging) return;

                        // **FIXED: Handle parent-level items**
                        if (!parentId) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const mouseY = e.clientY;
                            const itemMiddle = rect.top + rect.height / 2;

                            if (mouseY < itemMiddle) {
                                setActiveDropZone({ menuType, index });
                            } else {
                                setActiveDropZone({ menuType, index: index + 1, parentId: item.id });
                            }
                        }
                        // **FIXED: Handle child-level items - enable reordering**
                        else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const mouseY = e.clientY;
                            const itemMiddle = rect.top + rect.height / 2;

                            if (mouseY < itemMiddle) {
                                // Drop above this child
                                setActiveDropZone({
                                    menuType,
                                    index,
                                    parentId,
                                    isChildZone: true
                                });
                            } else {
                                // Drop below this child
                                setActiveDropZone({
                                    menuType,
                                    index: index + 1,
                                    parentId,
                                    isChildZone: true
                                });
                            }
                        }
                    }}
                >
                    <MenuItemCard
                        draggable
                        onDragStart={(e) => handleDragStart(e, item, { menuType, item, parentId })}
                        onDragEnd={handleDragEnd}
                    >
                        <ItemContent>
                            <DraggableIcon color={theme.CorporateTheme.colors.lightSecondary} />
                            <div className='d-flex align-items-center justify-content-between w-100' style={{ borderRadius: 5, padding: '15px 20px', backgroundColor: theme.CorporateTheme.colors.filterBG }}>
                                <div className='d-flex flex-column gap-1 '>
                                    <div className='d-flex align-items-center gap-2'>
                                        <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>{item?.name}</StyledText>
                                        {item?.pageType === "external" ? <Badge borderColor="none" padding='5px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.2)} color={theme.CorporateTheme.colors.primary} fontSize='10px' fontStyle='italic' fontWeight={600} text={capitalizeFirstLetter(item?.pageType!)} borderRadius='3px' /> : null}
                                    </div>

                                    <StyledText fontSize='12px' fontWeight={500} textColor={CreateFormTheme.colors.inputColor} >/{item?.url}</StyledText>
                                </div>
                                <div
                                    className='cursor-pointer'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteItem(menuType, item.id, parentId);
                                    }}
                                >
                                    <DeleteIcon size={18} color={theme.CorporateTheme.colors.gunmetal} />
                                </div>
                            </div>
                        </ItemContent>
                    </MenuItemCard>

                    {/* **FIXED: Render children with proper drop zones** */}
                    {item.children?.map((child, childIndex) => (
                        <div key={child.id} style={{ minHeight: isDragging ? '85px' : '0px', transition: 'min-height 0.2s' }}>
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isDragging) {
                                        setActiveDropZone({
                                            menuType,
                                            index: childIndex,
                                            parentId: item.id,
                                            isChildZone: true
                                        });
                                    }
                                }}
                            >
                                {renderChildDropZone(menuType, item.id, childIndex, item.children!.length)}
                            </div>

                            {/* The child item itself */}
                            {renderMenuItem(child, menuType, childIndex, item.children!, item.id)}
                        </div>
                    ))}

                    {/* **FIXED: Drop zone AFTER last child - shows BOTH zones** */}
                    {/* {item.children && item.children.length > 0 && (
                        <div
                            style={{ minHeight: isDragging ? '85px' : '0px', transition: 'min-height 0.2s' }}

                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDragging) {
                                    setActiveDropZone({
                                        menuType,
                                        index: item.children!.length,
                                        parentId: item.id,
                                        isChildZone: true
                                    });
                                }
                            }}
                        >

                            {isDragging && activeDropZone?.menuType === menuType &&
                                activeDropZone?.parentId === item.id &&
                                activeDropZone?.index === item.children!.length && (
                                    renderDropZonePair(menuType, item.children!.length, item.id, true)
                                )}
                        </div>
                    )} */}
                    {/* {item.children && item.children.length > 0 && (
                        <div
                            style={{ minHeight: isDragging ? '85px' : '0px', transition: 'min-height 0.2s' }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDragging) {
                                    setActiveDropZone({
                                        menuType,
                                        index: item.children!.length,
                                        parentId: item.id,
                                        isChildZone: true
                                    });
                                }
                            }}
                        >
                            {isDragging && renderDropZonePair(menuType, item.children.length, item.id, true)}
                        </div>
                    )} */}
                </MenuItemWrapper>
            </div>
        );
    };

    const renderSkeletons = () => (
        <div className="d-flex flex-column gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
                <SkeletonCell key={index} height="59px" />
            ))}
        </div>
    );

    const renderMenuSection = (
        title: string,
        subtitle: string,
        menuType: string,
        items: MenuItem[],
    ) => {
        const isExpanded = expandedMenus[menuType];

        return (
            <MenuSection theme={theme}>
                <MenuHeader
                    onClick={(e) => {
                        if (!isDragging) {
                            setExpandedMenus(prev => ({ ...prev, [menuType]: !prev[menuType] }));
                        }
                        e.stopPropagation();
                    }}
                >

                    <SecTitleWithIconDesc fontWeight={700} fontSize={'16px'} fontColor={theme.CorporateTheme.colors.primary} descfontSize={'14px'} icon={<MenuStackIcon color={theme.CorporateTheme.colors.primary} />} title={title} desc={subtitle} />
                    <ChevronDownIcon size={20} color={theme.CorporateTheme.colors.textSecondary} rotate={isExpanded ? "up" : "down"} className='cursor-pointer' />

                </MenuHeader>

                {isExpanded && (

                    <MenuContent
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isDragging && items.length === 0) {
                                setActiveDropZone({ menuType, index: 0 });
                            }
                        }}
                    >
                        <StyledHorizontalLine theme={theme} className='my-3' />

                        {/* 🔥 SKELETON STATE */}
                        {isMenuLoading ? renderSkeletons() : null}
                        {!isMenuLoading && items.length === 0 && !isDragging && (
                            <EmptyState>Drag pages here to add them to this menu</EmptyState>
                        )}

                        {!isMenuLoading && items.length === 0 && isDragging && renderDropZonePair(menuType, 0)}

                        {!isMenuLoading && items.map((item, index) => renderMenuItem(item, menuType, index, items))}
                        {!isMenuLoading && items.length > 0 && (
                            <div
                                className="h-24"
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isDragging) {
                                        setActiveDropZone({
                                            menuType,
                                            index: items.length,
                                            parentId: items[items.length - 1].id
                                        });
                                    }
                                }}
                            >
                                {renderDropZonePair(menuType, items.length, items[items.length - 1].id)}
                            </div>
                        )}
                    </MenuContent>
                )}
            </MenuSection>
        );
    };

    return (
        <Container>
            <LeftPanel theme={theme}>
                <PanelHeader>
                    <StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>All Published Pages</StyledText>
                    <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className='mt-1'>Custom & module based pages listed here</StyledText>
                </PanelHeader>
                <StyledHorizontalLine theme={theme} className='mx-3 mb-1' />
                <SearchWrapper>
                    <SearchBar
                        placeholder="Search or type a command"
                        style={{ width: '100%' }}
                        onSearchChange={handleSearchChange}
                        searchTerm={searchTerm}
                    />
                </SearchWrapper>

                <PageList>
                    {
                        isLoading || isFetching ? (
                            <div className="d-flex flex-column gap-3">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <SkeletonCell key={index} height="50px" />
                                ))}
                            </div>
                        ) :

                            publishedPages?.pages?.map((page: MenuItem) => (
                                <PageItem
                                    key={page.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, page)}
                                    onDragEnd={handleDragEnd}
                                    isDragging={draggedItem?.id === page.id}
                                    theme={theme}
                                >
                                    <div className='d-flex flex-column gap-1'>
                                        <div className="d-flex gap-2 align-items-center">
                                            <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>{page.name}</StyledText>
                                            {page?.pageType === "external" ? <Badge borderColor="none" padding='5px' bgColor={hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.2)} color={theme.CorporateTheme.colors.primary} fontSize='10px' fontStyle='italic' fontWeight={600} text={capitalizeFirstLetter(page?.pageType!)} borderRadius='3px' /> : null}
                                        </div>
                                        <StyledText fontSize='12px' fontWeight={500} textColor={CreateFormTheme.colors.inputColor}>/{page.url}</StyledText>
                                    </div>
                                    <div className="hover-icon">
                                        <DraggableIcon
                                            size={18}
                                            color={theme.CorporateTheme.colors.lightSecondary}
                                        />
                                    </div>
                                </PageItem>
                            ))}
                </PageList>
            </LeftPanel>

            <RightPanel
                ref={rightPanelRef}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (isDragging) handleAutoScroll(e);
                }}
                onDragLeave={stopAutoScroll}
                onDrop={stopAutoScroll}
            >

                {renderMenuSection(
                    'Utility Nav Menu',
                    'All added pages will appear in the website above primary menu',
                    'utility',
                    utilityMenu
                )}

                {renderMenuSection(
                    'Primary Menu (Header)',
                    'All added pages will appear in the website top navigation bar',
                    'primary',
                    primaryMenu
                )}

                {renderMenuSection(
                    'Secondary Menu (Footer)',
                    'All added pages will appear in the website footer',
                    'secondary',
                    secondaryMenu
                )}
            </RightPanel>
        </Container>
    );
});

export default MenuBuilder;